import { useState, useEffect, useCallback } from "react"
import { uploadedFileService } from "@/api/services"
import type { UploadedFileDto } from "@/api/types/uploadedFile.types"
import {
  DEFAULT_IMAGE_TYPES,
  DEFAULT_MAX_SIZE,
} from "@/api/types/uploadedFile.types"
import { validateFile } from "@/lib/fileValidation"
import { toast } from "sonner"

interface UseFileUploadOptions {
  initialFileId?: string
  allowedTypes?: string[]
  maxSize?: number
  onUploadSuccess?: (file: UploadedFileDto) => void
  onDeleteSuccess?: () => void
  onError?: (error: string) => void
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const {
    initialFileId,
    allowedTypes = DEFAULT_IMAGE_TYPES,
    maxSize = DEFAULT_MAX_SIZE,
    onUploadSuccess,
    onDeleteSuccess,
    onError,
  } = options

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadedFile, setUploadedFile] = useState<UploadedFileDto | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load initial file if fileId is provided
  useEffect(() => {
    if (initialFileId && !uploadedFile) {
      loadInitialFile(initialFileId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFileId])

  // Auto-upload when file is selected
  useEffect(() => {
    if (selectedFile && !uploading) {
      handleUpload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile])

  const loadInitialFile = async (fileId: string) => {
    try {
      const file = await uploadedFileService.getUploadedFile(fileId)
      setUploadedFile(file)
      setPreviewUrl(file.url)
    } catch (err: any) {
      console.error("Failed to load initial file:", err)
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to load file"
      setError(errorMessage)
      if (onError) {
        onError(errorMessage)
      }
    }
  }

  const handleFileSelect = useCallback(
    async (file: File | null) => {
      setError(null)

      if (!file) {
        // If file is null, delete existing uploaded file
        if (uploadedFile) {
          await handleDelete()
        }
        setSelectedFile(null)
        setPreviewUrl(null)
        return
      }

      // Validate file
      const validation = validateFile(file, {
        allowedTypes,
        maxSize,
      })

      if (!validation.valid) {
        const errorMsg = validation.error || "Invalid file"
        setError(errorMsg)
        toast.error(errorMsg)
        if (onError) {
          onError(errorMsg)
        }
        return
      }

      // Delete existing file if any
      if (uploadedFile?.id) {
        try {
          await uploadedFileService.deleteUploadedFile(uploadedFile.id)
        } catch (err) {
          // Silently fail if delete fails, continue with new upload
          console.warn("Failed to delete existing file:", err)
        }
        setUploadedFile(null)
        setPreviewUrl(null)
      }

      // Set new file (will trigger auto-upload via useEffect)
      setSelectedFile(file)
    },
    [uploadedFile, allowedTypes, maxSize, onError]
  )

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return

    setUploading(true)
    setError(null)

    try {
      const file = await uploadedFileService.uploadFile(selectedFile)
      setUploadedFile(file)
      setPreviewUrl(file.url)
      setSelectedFile(null) // Clear selected file after upload

      toast.success("File uploaded successfully")
      if (onUploadSuccess) {
        onUploadSuccess(file)
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to upload file"
      setError(errorMessage)
      toast.error(errorMessage)
      if (onError) {
        onError(errorMessage)
      }
      setSelectedFile(null)
      setPreviewUrl(null)
    } finally {
      setUploading(false)
    }
  }, [selectedFile, onUploadSuccess, onError])

  const handleDelete = useCallback(async () => {
    if (!uploadedFile?.id) return

    setDeleting(true)
    setError(null) // Clear any previous errors
    try {
      await uploadedFileService.deleteUploadedFile(uploadedFile.id)
      setUploadedFile(null)
      setPreviewUrl(null)
      setSelectedFile(null)
      if (onDeleteSuccess) {
        onDeleteSuccess()
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to delete file"
      setError(errorMessage)
      toast.error(errorMessage)
      if (onError) {
        onError(errorMessage)
      }
    } finally {
      setDeleting(false)
    }
  }, [uploadedFile, onDeleteSuccess, onError])

  const reset = useCallback(() => {
    setSelectedFile(null)
    setUploadedFile(null)
    setPreviewUrl(null)
    setError(null)
  }, [])

  const validateFileFn = useCallback(
    (file: File) => {
      return validateFile(file, { allowedTypes, maxSize })
    },
    [allowedTypes, maxSize]
  )

  return {
    // File state
    selectedFile,
    uploadedFile,
    previewUrl,
    fileId: uploadedFile?.id || null,

    // Status
    uploading,
    deleting,
    error,

    // Actions
    handleFileSelect,
    handleDelete,
    reset,

    // Validation
    validateFile: validateFileFn,

    // Helpers
    hasFile: !!selectedFile || !!uploadedFile,
    hasUploadedFile: !!uploadedFile,
  }
}

